import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Hero from '../components/Hero';
import ServiceCard from '../components/ServiceCard';
import USPItem from '../components/USPItem';
import BlogCard from '../components/BlogCard';
import { blogPosts, getFeaturedPosts } from '../data/blogData';

const Home = () => {
  const services = [
    {
      title: "Golf Academy",
      description: "Personalized coaching programs with certified PGA professionals. Transform your game through systematic training and modern techniques.",
      imageUrl: "https://www.marangaroogolf.com.au/wp-content/uploads/sites/28/2024/06/nathan-jennings-FmhOm8RjoW8-unsplash-scaled.jpg",
      icon: "🏌️"
    },
    {
      title: "Golf Simulators",
      description: "Cutting-edge technology with real-time swing analysis. Practice on world-class courses regardless of weather conditions.",
      imageUrl: "https://images.pexels.com/photos/31212256/pexels-photo-31212256.jpeg?cs=srgb&dl=pexels-swingzone-swingzone-2150575315-31212256.jpg&fm=jpg",
      icon: "📊"
    },
    {
      title: "Pro Shop",
      description: "Premium equipment with expert custom fitting. Find the perfect gear to enhance your performance on the course.",
      imageUrl: "https://images.unsplash.com/photo-1703293024102-44224053a305?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8Z29sZiUyMHNwb3J0fGVufDB8fDB8fHww",
      icon: "🛒"
    }
  ];

  const uspItems = [
    {
      number: "01",
      title: "Affordable Access",
      description: "Demystifying golf through innovative membership models and pay-per-play options that make golf accessible to everyone."
    },
    {
      number: "02",
      title: "International Standards",
      description: "World-class facilities and coaching methodologies that match global benchmarks for excellence."
    },
    {
      number: "03",
      title: "Technology-Driven",
      description: "Advanced simulators, data analytics, and AI-powered training for precision improvement."
    },
    {
      number: "04",
      title: "Personalized Coaching",
      description: "Tailored training programs that adapt to individual needs, goals, and skill levels."
    }
  ];

  // Get featured posts for home page
  const featuredBlogPosts = getFeaturedPosts();

  return (
    <>
      {/* Custom Hero Section with Video Background */}
      <section style={{
        position: 'relative',
        height: '100vh',
        overflow: 'hidden'
      }}>
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            minWidth: '100%',
            minHeight: '100%',
            width: 'auto',
            height: 'auto',
            transform: 'translate(-50%, -50%)',
            objectFit: 'cover',
            zIndex: 1
          }}
        >
          <source src="/images/golf.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(rgba(31, 61, 43, 0.7), rgba(31, 61, 43, 0.9))',
          zIndex: 2
        }}></div>

        {/* Hero Content */}
        <Container className="position-relative h-100 d-flex align-items-center" style={{ zIndex: 3 }}>
          <Row className="w-100">
            <Col lg={8} className="text-white">
              <div className="animate-fade-in-up">
                <h1 className="display-3 fw-bold mb-4" style={{ color: "#B59A5A" }}>
                  Making Golf Accessible. Worldwide.
                </h1>
                <p className="lead mb-4" style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                  Discover a place where peace and relaxation will surround you from the moment you step through our doors.
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <a href="/services" className="btn-gold-filled">Explore Services</a>
                  <a href="/academy" className="btn-gold">Join the Academy</a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        {/* Scroll Indicator */}
        <div className="position-absolute bottom-4 start-50 translate-middle-x text-white animate-bounce" style={{ zIndex: 5 }}>
          <div className="text-center">
            <div className="mb-2" style={{ fontSize: '0.9rem', opacity: 0.8 }}>Scroll to Explore</div>
            <div style={{ fontSize: '1.5rem' }}>↓</div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 fw-bold mb-3 animate-fade-in-up">Elevate Your Game</h2>
              <p className="lead animate-fade-in-up" style={{ color: '#666', animationDelay: '0.2s' }}>
                Comprehensive golf solutions designed to transform beginners into enthusiasts and enthusiasts into champions.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {services.map((service, index) => (
              <Col md={4} key={index}>
                <div className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <ServiceCard {...service} />
                </div>
              </Col>
            ))}
          </Row>

          {/* Additional Services Info */}
          <Row className="mt-5">
            <Col lg={8} className="mx-auto text-center">
              <div className="p-4 bg-soft-sage rounded animate-scale-in" style={{ animationDelay: '0.4s' }}>
                <h4 className="h5 mb-3">Complete Golf Ecosystem</h4>
                <p style={{ color: '#666' }}>
                  From your first swing to professional tournaments, we provide everything you need in one place.
                  Our integrated approach ensures seamless progression at every skill level.
                </p>
                <a href="/services" className="btn-gold-filled mt-3">Explore All Services</a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission & Vision Section */}
      <section className="section-padding bg-soft-sage position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{
          background: 'radial-gradient(circle at 20% 50%, rgba(181, 154, 90, 0.1) 0%, transparent 50%)',
          zIndex: 0
        }}></div>

        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="animate-slide-in-left">
                <div className="d-flex align-items-center mb-4">
                  <div style={{
                    width: '4px',
                    height: '40px',
                    backgroundColor: 'var(--muted-gold)',
                    marginRight: '15px'
                  }}></div>
                  <h2 className="mb-0">Our Mission & Vision</h2>
                </div>

                <div className="mb-5 p-4 bg-white rounded shadow-sm animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="d-flex align-items-start mb-3">
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'var(--muted-gold)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      flexShrink: 0
                    }}>
                      <span className="h5 mb-0">M</span>
                    </div>
                    <div>
                      <h3 className="h4 mb-2">The Golfer's Mission</h3>
                      <p style={{ color: '#666' }}>
                        To democratize golf in India by making it accessible and affordable for all enthusiasts,
                        breaking down traditional barriers while maintaining world-class standards of excellence.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded shadow-sm animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                  <div className="d-flex align-items-start mb-3">
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'var(--primary-green)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      flexShrink: 0
                    }}>
                      <span className="h5 mb-0">V</span>
                    </div>
                    <div>
                      <h3 className="h4 mb-2">The Golfer's Vision</h3>
                      <p style={{ color: '#666' }}>
                        To cultivate India's next generation of world-class golfers through innovative coaching,
                        cutting-edge technology, and a holistic development ecosystem that creates champions.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 d-flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                  <a href="/academy" className="btn-gold-filled">Join Our Academy</a>
                  <a href="/contact" className="btn-gold">Schedule a Tour</a>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="animate-slide-in-right">
                <div style={{
                  height: '500px',
                  background: 'url(https://images.unsplash.com/photo-1535131749006-b7f58c99034b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80) center/cover no-repeat',
                  borderRadius: '12px',
                  boxShadow: '0 20px 60px rgba(31, 61, 43, 0.2)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                    background: 'linear-gradient(135deg, rgba(31, 61, 43, 0.1) 0%, rgba(31, 61, 43, 0.3) 100%)'
                  }}></div>

                  <div className="position-absolute top-4 start-4 animate-float" style={{ animationDelay: '1s' }}>
                    <div className="bg-white rounded-pill px-3 py-2 shadow-sm d-flex align-items-center">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: 'var(--muted-gold)',
                        borderRadius: '50%',
                        marginRight: '8px'
                      }}></div>
                      <span className="small fw-bold" style={{ color: 'var(--primary-green)' }}>Since 2022</span>
                    </div>
                  </div>

                  <div className="position-absolute bottom-4 end-4 animate-float" style={{ animationDelay: '1.2s' }}>
                    <div className="bg-white rounded-pill px-3 py-2 shadow-sm d-flex align-items-center">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: 'var(--muted-gold)',
                        borderRadius: '50%',
                        marginRight: '8px'
                      }}></div>
                      <span className="small fw-bold" style={{ color: 'var(--primary-green)' }}>1000+ Students</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why G4I Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 fw-bold mb-3 animate-fade-in-up">Why Choose Golf4International</h2>
              <p className="lead animate-fade-in-up" style={{ color: '#666', animationDelay: '0.2s' }}>
                Our unique approach combines tradition with innovation to deliver exceptional golf experiences.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {uspItems.map((item, index) => (
              <Col lg={6} key={index}>
                <div className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="p-4 h-100 border rounded hover-lift" style={{
                    transition: 'all 0.3s ease',
                    borderColor: 'rgba(31, 61, 43, 0.1)'
                  }}>
                    <USPItem {...item} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Stats Section */}
          <Row className="mt-5 pt-5 border-top">
            {[
              { value: "1000+", label: "Students Trained", delay: "0s", icon: "👨‍🎓" },
              { value: "24/7", label: "Simulator Access", delay: "0.1s", icon: "🖥️" },
              { value: "15+", label: "Certified Coaches", delay: "0.2s", icon: "🏌️" },
              { value: "98%", label: "Satisfaction Rate", delay: "0.3s", icon: "⭐" }
            ].map((stat, index) => (
              <Col md={3} className="text-center mb-4" key={index}>
                <div className="animate-count-up" style={{ animationDelay: stat.delay }}>
                  <div style={{
                    width: '70px',
                    height: '70px',
                    backgroundColor: 'var(--soft-sage)',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                    marginBottom: '1rem'
                  }}>
                    {stat.icon}
                  </div>
                  <h3 className="h1 mb-2" style={{ color: 'var(--muted-gold)' }}>{stat.value}</h3>
                  <p style={{ color: '#666' }}>{stat.label}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Blog Preview Section */}
      <section className="section-padding bg-soft-sage">
        <Container>
          <Row className="mb-5">
            <Col lg={8}>
              <h2 className="display-5 fw-bold mb-3">Latest Golf Insights</h2>
              <p style={{ color: '#666' }}>
                Stay updated with expert articles, tips, and news from the world of golf
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <a href="/blog" className="btn-gold">View All Articles</a>
            </Col>
          </Row>

          <Row className="g-4">
            {featuredBlogPosts.map((post) => (
              <Col md={4} key={post.id}>
                <BlogCard {...post} />
              </Col>
            ))}
          </Row>

          {/* Blog Categories Preview */}
          <Row className="mt-5">
            <Col className="text-center">
              <div className="p-5 bg-white rounded">
                <h3 className="h4 mb-4">Explore More Topics</h3>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  {["Technology", "Academy", "Business", "Sustainability"].map((category, index) => (
                    <a
                      key={index}
                      href={`/blog?category=${category}`}
                      className="btn-gold"
                      style={{ padding: '10px 24px', borderRadius: '25px' }}
                    >
                      {category}
                    </a>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          {/* Newsletter Signup */}
          <Row className="mt-5">
            <Col lg={8} className="mx-auto">
              <div className="p-5 bg-white rounded text-center">
                <div className="mb-4">
                  <div style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: 'var(--soft-sage)',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <span style={{ fontSize: '2rem', color: 'var(--muted-gold)' }}>✉️</span>
                  </div>
                  <h3 className="h4 mb-3">Get Golf Tips & Updates</h3>
                  <p style={{ color: '#666' }} className="mb-4">
                    Subscribe to our newsletter for exclusive golf tips, industry insights, tournament updates,
                    and special offers delivered to your inbox.
                  </p>
                </div>
                <div className="input-group mx-auto" style={{ maxWidth: '500px' }}>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email address"
                    style={{ padding: '12px' }}
                  />
                  <button className="btn-gold-filled" type="button">
                    Subscribe
                  </button>
                </div>
                <p className="small text-muted mt-3 mb-0">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonial Section */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 fw-bold mb-3">What Our Members Say</h2>
              <p className="lead" style={{ color: '#666' }}>
                Join hundreds of satisfied golfers who have transformed their game with us
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              {
                name: "Rajesh Kumar",
                role: "Beginner to Intermediate",
                testimonial: "The systematic coaching approach transformed my game completely. From a complete beginner to now playing confidently in tournaments.",
                rating: 5
              },
              {
                name: "Priya Sharma",
                role: "Corporate Executive",
                testimonial: "The simulators are fantastic for practice during weekdays. The data analytics helped me understand my swing better than ever before.",
                rating: 5
              },
              {
                name: "Arvind Patel",
                role: "Junior Golfer Parent",
                testimonial: "My son's progress in the youth development program has been incredible. The coaches are amazing with kids and very professional.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <Col md={4} key={index}>
                <div className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="p-4 border rounded h-100 hover-lift" style={{
                    transition: 'all 0.3s ease',
                    borderColor: 'rgba(31, 61, 43, 0.1)'
                  }}>
                    <div className="d-flex align-items-center mb-3">
                      <div style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: 'var(--soft-sage)',
                        borderRadius: '50%',
                        marginRight: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}>
                        <span>👤</span>
                      </div>
                      <div>
                        <h5 className="h6 mb-1">{testimonial.name}</h5>
                        <p className="small text-muted mb-0">{testimonial.role}</p>
                      </div>
                    </div>
                    <div className="mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} style={{ color: 'var(--muted-gold)', marginRight: '2px' }}>★</span>
                      ))}
                    </div>
                    <p style={{ color: '#666' }} className="mb-0">"{testimonial.testimonial}"</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Global Vision Section */}
      <section className="section-padding bg-soft-sage position-relative overflow-hidden">
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{
          background: 'url(https://images.unsplash.com/photo-1579113800032-c38bd7635818?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80) center/cover no-repeat fixed',
          opacity: 0.1,
          zIndex: 0
        }}></div>

        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="animate-slide-in-left">
                <h2 className="display-5 fw-bold mb-4">Global Vision & Scale</h2>

                <div className="d-flex flex-wrap gap-4 mb-4">
                  <div className="text-center animate-scale-in" style={{ animationDelay: '0.2s' }}>
                    <div className="rounded-circle p-3 mb-2" style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: 'var(--muted-gold)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span className="h4 text-white mb-0">🇮🇳</span>
                    </div>
                    <h4 className="h6 mb-1">India First</h4>
                    <p className="small mb-0" style={{ color: '#666' }}>Homegrown Excellence</p>
                  </div>

                  <div className="text-center animate-scale-in" style={{ animationDelay: '0.3s' }}>
                    <div className="rounded-circle p-3 mb-2" style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: 'var(--primary-green)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span className="h4 text-white mb-0">🌍</span>
                    </div>
                    <h4 className="h6 mb-1">Global Standards</h4>
                    <p className="small mb-0" style={{ color: '#666' }}>World-Class Quality</p>
                  </div>
                </div>

                <p className="mb-4 animate-fade-in-up" style={{ animationDelay: '0.5s', color: '#666' }}>
                  Delivering premium golf experiences worldwide through cutting-edge technology and international expertise.
                  Our expansion strategy focuses on bringing world-class facilities to major cities across India.
                </p>

                <div className="d-flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                  <div className="bg-white rounded p-3 flex-grow-1" style={{ minWidth: '200px' }}>
                    <h5 className="h6 mb-2" style={{ color: 'var(--primary-green)' }}>Current Locations</h5>
                    <p className="small mb-0" style={{ color: '#666' }}>
                      <span className="d-block">🏢 Delhi Headquarters</span>
                      <span className="d-block">🏢 Mumbai Center</span>
                      <span className="d-block">🏢 Bengaluru Hub</span>
                    </p>
                  </div>

                  <div className="bg-white rounded p-3 flex-grow-1" style={{ minWidth: '200px' }}>
                    <h5 className="h6 mb-2" style={{ color: 'var(--muted-gold)' }}>Coming Soon</h5>
                    <p className="small mb-0" style={{ color: '#666' }}>
                      <span className="d-block">📍 Chennai 2024</span>
                      <span className="d-block">📍 Kolkata 2024</span>
                      <span className="d-block">📍 Hyderabad 2025</span>
                    </p>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <div className="animate-slide-in-right">
                <div style={{
                  height: '400px',
                  background: 'url(https://images.unsplash.com/photo-1592659762303-90081d34b277?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80) center/cover no-repeat',
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div className="position-absolute bottom-0 start-0 end-0 p-4 text-white" style={{
                    background: 'linear-gradient(transparent, rgba(31, 61, 43, 0.9))'
                  }}>
                    <h4 className="h5 mb-2">International Partnerships</h4>
                    <p className="small mb-0" style={{ opacity: 0.9 }}>
                      Collaborating with global golf institutions for excellence
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes countUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.6s ease-out forwards;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.6s ease-out forwards;
        }
        
        .animate-scale-in {
          animation: scaleIn 0.6s ease-out forwards;
        }
        
        .animate-count-up {
          animation: countUp 0.6s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
        
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(31, 61, 43, 0.1);
          transition: all 0.3s ease;
        }
      `}</style>
    </>
  );
};

export default Home;